import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { ActivityLogItem } from './ActivityLogItem';
import { CommentItem } from './CommentItem';
import { groupActivityByDay, getActivityDayLabel } from '../../utils/group-activity-by-day';
import type { FeedItem, TaskCommentDto } from '../../types';

interface ActivityLogSectionProps {
  items: FeedItem[];
  totalCount: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  currentUserId: string | null;
  onReply: (commentId: string, authorName: string) => void;
  onDeleteComment: (commentId: string) => void;
}

export function ActivityLogSection({
  items,
  totalCount,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  onLoadMore,
  currentUserId,
  onReply,
  onDeleteComment,
}: ActivityLogSectionProps) {
  const { t } = useTranslation('tasks');
  const [expanded, setExpanded] = useState(true);

  const groups = groupActivityByDay(items, (item) => item.data.createdAt);

  return (
    <section data-testid="tasks-activity-history-section">
      <button
        type="button"
        className="flex w-full items-center gap-1.5 px-4 py-2 text-left transition-colors hover:bg-muted/40"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        data-testid="tasks-activity-history-toggle"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
        )}
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t('activity.historyTitle')}
        </span>
        {totalCount > 0 && (
          <span
            className="ml-auto rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
            data-testid="tasks-activity-history-badge"
          >
            {totalCount}
          </span>
        )}
      </button>

      {expanded && (
        <div className="px-4">
          {isLoading && (
            <div className="flex flex-col gap-2.5 py-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex gap-2.5">
                  <Skeleton className="h-6 w-6 shrink-0 rounded-full" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3 w-3/4" />
                    <Skeleton className="h-2.5 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isLoading && items.length === 0 && (
            <p className="py-3 text-xs text-muted-foreground" data-testid="tasks-activity-empty">
              {t('activity.empty')}
            </p>
          )}

          {!isLoading && items.length > 0 && (
            <div data-testid="tasks-activity-list">
              {groups.map(({ key, items: groupItems }) => (
                <div key={key}>
                  <div
                    className="flex items-center gap-2 py-2"
                    data-testid="tasks-activity-date-divider"
                  >
                    <span className="whitespace-nowrap text-[10px] font-medium text-muted-foreground/70">
                      {getActivityDayLabel(t, key)}
                    </span>
                    <div className="h-px flex-1 bg-border/50" />
                  </div>
                  {groupItems.map((item) => {
                    if (item.kind === 'activity') {
                      return (
                        <ActivityLogItem
                          key={item.data.id ?? item.data.createdAt}
                          activity={item.data}
                        />
                      );
                    }
                    return (
                      <div key={item.data.id ?? item.data.createdAt}>
                        <CommentItem
                          comment={item.data}
                          currentUserId={currentUserId}
                          onReply={onReply}
                          onDelete={onDeleteComment}
                        />
                        {item.replies.map((reply: TaskCommentDto) => (
                          <CommentItem
                            key={reply.id ?? reply.createdAt}
                            comment={reply}
                            currentUserId={currentUserId}
                            isReply
                            onReply={onReply}
                            onDelete={onDeleteComment}
                          />
                        ))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {hasNextPage && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-1 h-7 w-full text-xs text-muted-foreground"
              onClick={onLoadMore}
              disabled={isFetchingNextPage}
              data-testid="tasks-activity-load-more"
            >
              {isFetchingNextPage ? t('loading') : t('activity.loadMore')}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
